// Mass-import (manifest CSV) API client.
//
// Backend lives at /api/admin/bulk-imports — see api/cmd/server/main.go
// for why the prefix is bulk-imports/ rather than imports/bulk/ (the
// JFrog handler claims the imports/ subtree).

import useSWR from 'swr';

export type BulkImportStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export type BulkRowState =
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'skipped';

export interface BulkImportJob {
  id: string;
  name: string;
  mode: 'manifest_csv' | 'tarball';
  status: BulkImportStatus;
  error: string;
  rows_total: number;
  rows_done: number;
  rows_failed: number;
  rows_skipped: number;
  bytes_done: number;
  started_at: string;
  finished_at?: string | null;
  started_by: string;
  cancel_requested: boolean;
}

export interface BulkImportRow {
  id: number;
  row_num: number;
  format: string;
  project_slug: string;
  repo_slug: string;
  upstream_url: string;
  target_path: string;
  version: string;
  state: BulkRowState;
  error: string;
  bytes: number;
  attempts: number;
  claimed_by: string;
  started_at?: string | null;
  finished_at?: string | null;
}

export interface BulkImportDetail extends BulkImportJob {
  artifacts: BulkImportRow[];
}

const fetcher = (url: string) =>
  fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

// Phase D long-operation polling — only poll while at least one job
// is live (pending|running). Once every job has terminated, stop the
// 5 s tick so the admin tab doesn't hammer the backend forever just
// to redraw a list of completed rows.
function listRefreshInterval(jobs?: BulkImportJob[]): number {
  if (!jobs) return 5_000;
  return jobs.some((j) => j.status === 'pending' || j.status === 'running') ? 5_000 : 0;
}

export function useBulkImports() {
  return useSWR<BulkImportJob[]>('/api/admin/bulk-imports', fetcher, {
    refreshInterval: (latest) => listRefreshInterval(latest),
  });
}

function detailRefreshInterval(detail?: BulkImportDetail): number {
  if (!detail) return 5_000;
  return detail.status === 'pending' || detail.status === 'running' ? 5_000 : 0;
}

export function useBulkImport(id: string | undefined) {
  return useSWR<BulkImportDetail>(
    id ? `/api/admin/bulk-imports/${id}` : null,
    fetcher,
    { refreshInterval: (latest) => detailRefreshInterval(latest) },
  );
}

export async function startBulkImport(
  manifest: File,
  name?: string,
): Promise<{ id: string; rows_total: number }> {
  const fd = new FormData();
  fd.append('manifest', manifest);
  if (name) fd.append('name', name);
  const res = await fetch('/api/admin/bulk-imports', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

// Phase-2 tarball mode. The operator picks a target (project, repo,
// format) and uploads a .tar / .tar.gz / .tgz; the backend extracts
// every regular file as one artifact in the target repo.
//
// The promise only resolves after the whole tarball has been
// processed — this is a long-running synchronous call, intentionally
// so that the operator sees the per-row results immediately on
// completion rather than polling. The fetch() call has no client-side
// timeout, so progress is dictated by the upload-and-process speed
// of the API node.
export interface StartBulkTarballArgs {
  tarball: File;
  projectSlug: string;
  repoSlug: string;
  format: string;
  name?: string;
}

export async function startBulkTarballImport(
  args: StartBulkTarballArgs,
): Promise<{ id: string }> {
  const fd = new FormData();
  fd.append('tarball', args.tarball);
  fd.append('project_slug', args.projectSlug);
  fd.append('repo_slug', args.repoSlug);
  fd.append('format', args.format);
  if (args.name) fd.append('name', args.name);
  const res = await fetch('/api/admin/bulk-imports/tarball', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

export async function cancelBulkImport(id: string): Promise<void> {
  const res = await fetch(`/api/admin/bulk-imports/${id}/cancel`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(await res.text());
}

export async function retryBulkFailed(id: string): Promise<void> {
  const res = await fetch(`/api/admin/bulk-imports/${id}/retry-failed`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok && res.status !== 204) throw new Error(await res.text());
}
